import type { AuthState } from '@/app/providers/auth'
import type {
  MenuAvailabilityFragment,
  MenuAvailabilityWithItemsPagedFragment,
} from '@/generated/graphql'
import type { Replace } from '@caterdesk/utils--gm-validation/types'
import { isNotNil } from '@caterdesk/utils--gm-validation/types'

import { formatInTimeZone } from 'date-fns-tz/formatInTimeZone'
import { format } from 'date-fns/format'
import { isValid } from 'date-fns/isValid'
import { parseISO } from 'date-fns/parseISO'

import type { MenuCategory } from './menu-category'
import { createMenuCategoriesOptimized } from './menu-category'
import type { MenuFilterCategory, SelectedMenuFilterCategory } from './menu-category-filter'
import { createMenuFilterCategories } from './menu-category-filter'
import type { MenuItem } from './menu-item'
import { createMenuItems } from './menu-item'
import type { MenuItemFilter, MenuItemFilterSections } from './menu-item-filter'
import { createMenuItemFilters } from './menu-item-filter'
import type { MenuVendorCategory } from './menu-vendor'

export type MenuAvailability = Replace<
  MenuAvailabilityFragment,
  {
    menuCategories: MenuCategory[]
    filterCategories: MenuFilterCategory[]
    filterSections: MenuItemFilterSections
    items: MenuItem[]
  }
>

export function itemAvailabilityFilter(
  item: Pick<MenuItem, 'activeFrom' | 'activeTo'>,
  deliveryDate: number | undefined,
  vendorTimezone: string | undefined,
): boolean {
  // Location selection may still be loading. Do not guess a timezone and hide items.
  if (!vendorTimezone) {
    return true
  }

  const reference = deliveryDate === undefined ? new Date() : new Date(deliveryDate)
  if (!isValid(reference)) {
    return false
  }

  // A selection carries browser calendar fields. Only "today" is derived at the vendor location.
  const day =
    deliveryDate === undefined
      ? formatInTimeZone(reference, vendorTimezone, 'yyyy-MM-dd')
      : format(reference, 'yyyy-MM-dd')

  for (const [endpoint, isStart] of [
    [item.activeFrom, true],
    [item.activeTo, false],
  ] as const) {
    if (!endpoint) {
      continue
    }

    const date = parseISO(endpoint)
    if (!isValid(date)) {
      return false
    }

    const endpointDay = formatInTimeZone(date, vendorTimezone, 'yyyy-MM-dd')
    if (isStart ? day < endpointDay : day > endpointDay) {
      return false
    }
  }

  return true
}

type CategoryFilterData = {
  hasCategories: boolean
  hasEverything: boolean
  hasHotDishes: boolean
  hasColdDishes: boolean
  numericIds: Set<number>
}

const prepareCategoryFilterData = (
  selectedCategories: SelectedMenuFilterCategory[],
): CategoryFilterData => {
  if (!selectedCategories || selectedCategories.length === 0) {
    return {
      hasCategories: false,
      hasEverything: false,
      hasHotDishes: false,
      hasColdDishes: false,
      numericIds: new Set(),
    }
  }

  const numericIds = new Set<number>()
  let hasEverything = false
  let hasHotDishes = false
  let hasColdDishes = false

  for (const category of selectedCategories) {
    if (category.id === 'everything') hasEverything = true
    else if (category.id === 'hot_dishes') hasHotDishes = true
    else if (category.id === 'cold_dishes') hasColdDishes = true
    else {
      const numId = Number(category.id)
      if (!Number.isNaN(numId)) numericIds.add(numId)
    }
  }

  return {
    hasCategories: true,
    hasEverything,
    hasHotDishes,
    hasColdDishes,
    numericIds,
  }
}

const matchesSearch = (item: MenuItem, queryLower: string): boolean => {
  if (!queryLower) return true

  const tokens = queryLower.split(/\s+/).filter(Boolean)
  if (!tokens.length) return true

  const name = (item.name || '').toLowerCase()
  const shortDescription = (item.shortDescription || '').toLowerCase()
  const description = (item.description || '').toLowerCase()
  const optionsText = (item.options || [])
    .flatMap((opt) => opt.items || [])
    .map((optItem) => {
      const optionName = optItem.item?.name || ''
      const optionShortDescription = optItem.item?.shortDescription || ''
      const optionDescription = optItem.item?.description || ''
      return `${optionName} ${optionShortDescription} ${optionDescription}`
    })
    .join(' ')
    .toLowerCase()

  let matchedTokens = 0

  for (const token of tokens) {
    if (
      token &&
      (name.includes(token) ||
        shortDescription.includes(token) ||
        description.includes(token) ||
        optionsText.includes(token))
    ) {
      matchedTokens += 1
    }
  }

  const requiredMatches = tokens.length === 1 ? 1 : Math.ceil(tokens.length * 0.5)

  return matchedTokens >= requiredMatches
}

const createItemFilter = (
  useServerSideItems: boolean | undefined,
  deliveryDate: number | undefined,
  vendorTimezone: string | undefined,
  authState: AuthState,
  accountId: number | null | undefined,
  categoryFilterData: CategoryFilterData,
  itemNameLower: string,
  allFilters: MenuItemFilter[],
) => {
  return (item: MenuItem): { passesBaseline: boolean; passesAllFilters: boolean } => {
    if (useServerSideItems) {
      if (!itemAvailabilityFilter(item, deliveryDate, vendorTimezone)) {
        return { passesBaseline: false, passesAllFilters: false }
      }

      if (item.isPrivate && authState.type === 'unauthenticated') {
        return { passesBaseline: false, passesAllFilters: false }
      }
      if (
        item.isPrivate &&
        accountId != null &&
        Array.isArray(item.privateAccessAccountIdArray) &&
        item.privateAccessAccountIdArray.length > 0 &&
        !item.privateAccessAccountIdArray.includes(accountId)
      ) {
        return { passesBaseline: false, passesAllFilters: false }
      }
    }

    const passesBaseline = true

    if (categoryFilterData.hasCategories) {
      if (!categoryFilterData.hasEverything) {
        let passesCategory = false

        if (categoryFilterData.hasHotDishes && item.isHot) {
          passesCategory = true
        } else if (categoryFilterData.hasColdDishes && !item.isHot) {
          passesCategory = true
        } else if (categoryFilterData.numericIds.size > 0 && item.categoryIdArray) {
          for (const catId of item.categoryIdArray) {
            if (categoryFilterData.numericIds.has(catId)) {
              passesCategory = true
              break
            }
          }
        }

        if (!passesCategory) {
          return { passesBaseline, passesAllFilters: false }
        }
      }
    }

    if (itemNameLower && !matchesSearch(item, itemNameLower)) {
      return { passesBaseline, passesAllFilters: false }
    }

    if (allFilters.length > 0) {
      for (const filter of allFilters) {
        if (!filter.predicate(item)) {
          return { passesBaseline, passesAllFilters: false }
        }
      }
    }

    return { passesBaseline, passesAllFilters: true }
  }
}

const withSelectedCategoryScope = (item: MenuItem, numericSelectedIds: Set<number>): MenuItem => {
  if (!item.categoryIdArray?.length || numericSelectedIds.size === 0) return item

  const intersectingIds = item.categoryIdArray.filter((id) => numericSelectedIds.has(id))
  if (intersectingIds.length === 0 || intersectingIds.length === item.categoryIdArray.length) {
    return item
  }

  return {
    ...item,
    categoryIdArray: intersectingIds,
  }
}

export const createMenuAvailability = ({
  availability,
  vendorCategories,
  selectedCategories,
  useServerSideItems,
  filters,
  deliveryDate,
  vendorTimezone,
  authState,
  accountId,
}: {
  availability: MenuAvailabilityWithItemsPagedFragment
  vendorCategories: MenuVendorCategory[]
  selectedCategories: SelectedMenuFilterCategory[]
  useServerSideItems?: boolean
  deliveryDate?: number
  vendorTimezone?: string
  authState: AuthState
  accountId?: number | null
  filters: {
    itemName?: string
    goals: MenuItemFilter[]
    dietaries: MenuItemFilter[]
    allergens: MenuItemFilter[]
  }
}): MenuAvailability => {
  const rawItems = (availability.itemsPagedv2.items ?? []).filter(isNotNil)
  const menuItems = createMenuItems(rawItems)

  const categoryFilterData = prepareCategoryFilterData(selectedCategories)
  const itemNameLower = filters.itemName?.toLowerCase() || ''
  const allFilters = [...filters.goals, ...filters.dietaries, ...filters.allergens]

  // Create the combined filter function
  const itemFilter = createItemFilter(
    useServerSideItems,
    deliveryDate,
    vendorTimezone,
    authState,
    accountId,
    categoryFilterData,
    itemNameLower,
    allFilters,
  )

  const baselineItemsMap = new Map<string, MenuItem>()
  const filteredItemsMap = new Map<string, MenuItem>()

  for (const item of menuItems) {
    const key = item._id || item.variationId
    if (!key) continue

    const { passesBaseline, passesAllFilters } = itemFilter(item)

    if (passesBaseline && !baselineItemsMap.has(key)) {
      baselineItemsMap.set(key, item)
    }

    if (passesAllFilters && !filteredItemsMap.has(key)) {
      const scopedItem = withSelectedCategoryScope(item, categoryFilterData.numericIds)
      filteredItemsMap.set(key, scopedItem)
    }
  }

  const uniqueBaselineItems = Array.from(baselineItemsMap.values())
  const uniqueItems = Array.from(filteredItemsMap.values())

  const stats = availability.itemsPagedv2.stats

  const categoriesWithCounts: Record<string, number> = useServerSideItems
    ? Object.fromEntries(stats.categories.map((c) => [c.label, c.value]))
    : {}

  const dietariesAndAllergensWithCounts: Record<string, number> = useServerSideItems
    ? Object.fromEntries([...stats.allergens, ...stats.dietaries].map((d) => [d.name, d.value]))
    : {}

  const allMenuCategories = createMenuCategoriesOptimized(
    uniqueBaselineItems,
    uniqueItems,
    vendorCategories,
  )

  const displayedMenuCategories: MenuCategory[] = []
  for (const category of allMenuCategories) {
    if (category && category.items.length > 0) {
      category.items.sort((a, b) => {
        const posA = a.position ?? Number.MAX_SAFE_INTEGER
        const posB = b.position ?? Number.MAX_SAFE_INTEGER
        return posA - posB
      })
      displayedMenuCategories.push(category)
    }
  }

  return {
    ...availability,
    menuCategories: displayedMenuCategories,
    filterCategories: createMenuFilterCategories(allMenuCategories, categoriesWithCounts, {
      useStatsCounts: false,
    }),
    filterSections: createMenuItemFilters(allMenuCategories, dietariesAndAllergensWithCounts, {
      useStatsCounts: false,
    }),
    items: uniqueItems,
  }
}
